import { useEffect, useRef, useState } from "react";

export default function VoiceWaveform({ isRecording, stream }) {
  const containerRef = useRef(null);
  const [wave, setWave] = useState([]);
  const rafRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const prevVolume = useRef(0);
  const frameCount = useRef(0);

  // 🧱 Création des barres
  useEffect(() => {
    if (!containerRef.current) return;
    const width = containerRef.current.offsetWidth - 20;
    const barWidth = 2;
    const gap = 2;
    const barCount = Math.floor(width / (barWidth + gap));
    const initialWave = Array(barCount)
      .fill()
      .map(() => ({
        color: "rgba(255,255,255,0.2)",
        height: 1,
      }));
    setWave(initialWave);
  }, []);

  // 🎧 Analyse du flux audio
  useEffect(() => {
    if (!isRecording || !stream) return;

    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);

    audioCtxRef.current = audioCtx;
    analyserRef.current = analyser;
    const dataArray = new Uint8Array(analyser.fftSize);

    const animate = () => {
      frameCount.current++;
      // on limite le rafraîchissement à ~15 fps
      if (frameCount.current % 2 === 0) {
        analyser.getByteTimeDomainData(dataArray);

        // RMS (volume)
        let sumSquares = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128;
          sumSquares += val * val;
        }
        const rms = Math.sqrt(sumSquares / dataArray.length);
        let volume = Math.min(rms * 8, 1); // moins sensible
        volume = prevVolume.current * 0.8 + volume * 0.2;
        prevVolume.current = volume;

        const height = Math.max(1, Math.round(volume * 15)); // plage 1–15 px

        setWave((prev) => {
          const next = [...prev];
          next.push({ color: "#fff", height });
          next.shift();
          return next;
        });
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
      }
      prevVolume.current = 0;
      frameCount.current = 0;
    };
  }, [isRecording, stream]);

  // 🌙 Idle mode (très lent)
  useEffect(() => {
    if (isRecording) return;
    const idle = setInterval(() => {
      setWave((prev) => {
        const next = [...prev];
        next.push({
          color: "rgba(255,255,255,0.2)",
          height: 1,
        });
        next.shift();
        return next;
      });
    }, 80);
    return () => clearInterval(idle);
  }, [isRecording]);

  return (
    <div
      ref={containerRef}
      className="my-[12px] flex items-center justify-center h-8 overflow-hidden w-full bg-transparent"
      style={{ willChange: "transform" }}
    >
      {wave.map((bar, i) => (
        <div
          key={i}
          style={{
            backgroundColor: bar.color,
            width: "2px",
            height: `${bar.height}px`,
            marginRight: "2px",
            boxShadow: `0 ${bar.height}px 0 0 ${bar.color}, 0 -${bar.height}px 0 0 ${bar.color}`,
          }}
        />
      ))}
    </div>
  );
}
